import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { tableQueryOptions } from "@/web/lib/tanstack";
import { tables } from "@tycho/web-bindings";
import {
  QuickbooksIcon,
  SquareIcon,
  StripeIcon,
  XeroIcon,
  MergeIcon,
  PaychexIcon,
} from "@/web/assets";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/web/components/primitives/card";
import { LinkedRutterAccounts } from "@/web/routes/_protected/connect/-components/LinkedRutterAccounts";
import { RutterLinkButton } from "@/web/routes/_protected/connect/-components/RutterLinkButton";
import { LinkedPlaidAccounts } from "@/web/routes/_protected/connect/-components/LinkedPlaidAccounts";
import { LinkedMergeAccounts } from "@/web/routes/_protected/connect/-components/LinkedMergeAccounts";
import { Leases } from "@/web/routes/_protected/connect/-components/Leases";
import { MergeLinkButton } from "@/web/routes/_protected/connect/-components/MergeLinkButton";
import { PlaidLinkButton } from "@/web/routes/_protected/connect/-components/PlaidLinkButton";
import { TaxReturns } from "@/web/routes/_protected/connect/-components/TaxReturns";

export const Route = createFileRoute("/_protected/connect/")({
  component: ConnectPage,
});

const allUnderwritableEntitiesOptions = tableQueryOptions(tables.allUnderwritableEntitiesForUser);

function ConnectPage() {
  // NOTE (multi-brand): this standalone `/connect` page is NOT currently linked
  // from anywhere in the app — the live connect flow runs through the SMB
  // dashboard's brand-scoped Connect* actions, which receive an explicit
  // `underwritableEntity` prop. This page still hardcodes `[0]` (the first
  // brand's first entity), so a multi-brand user would connect everything to
  // their *first* brand. Before re-linking this route, scope it to the selected
  // brand (thread the brand-switcher selection in, same as the dashboard) —
  // otherwise financial connections land on the wrong business. See
  // docs/MULTI_USER_BRANDS.md.
  const { data: underwritableEntities } = useSuspenseQuery(allUnderwritableEntitiesOptions);
  const defaultUnderwritableEntity = underwritableEntities[0];

  if (!defaultUnderwritableEntity) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 py-10">
        <div className="space-y-2">
          <h1 className="withus-strong text-3xl">Connect Financial Accounts</h1>
          <p className="text-destructive">
            Sorry, something went wrong. No underwritable entity exists for this account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 py-10">
      <div className="space-y-2">
        <h1 className="withus-strong text-3xl">Connect Financial Accounts</h1>
        <p className="text-muted-foreground">
          Connect your accounting, banking, and point of sale system and upload your tax returns and
          leases to securely share your financial data with us.
        </p>
      </div>

      <Card>
        <CardHeader className="py-4">
          <CardTitle>Accounting Software</CardTitle>
          <CardDescription>Share read-only access to your accounting data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <RutterLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                accountType="accounting"
                platformSlug="QUICKBOOKS"
                logoComponent={<QuickbooksIcon className="text-[#2CA01C]" />}
              />
              <RutterLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                accountType="accounting"
                platformSlug="XERO"
                logoComponent={<XeroIcon className="text-[#13B5EA]" />}
              />
              <RutterLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                accountType="accounting"
              />
            </div>
            <LinkedRutterAccounts accountType="accounting" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-4">
          <CardTitle>Bank Accounts</CardTitle>
          <CardDescription>Share read-only access to your bank account data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <PlaidLinkButton underwritableEntity={defaultUnderwritableEntity.id} />
            <LinkedPlaidAccounts />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-4">
          <CardTitle>Point of Sale System</CardTitle>
          <CardDescription>Share read-only access to your point of sale data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <RutterLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                accountType="pos"
                platformSlug="SQUARE"
                logoComponent={<SquareIcon className="text-[#3E4348]" />}
              />
              <RutterLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                accountType="pos"
                platformSlug="STRIPE"
                logoComponent={<StripeIcon className="text-[#635BFF]" />}
              />
              <RutterLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                accountType="pos"
              />
            </div>
            <LinkedRutterAccounts accountType="pos" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-4">
          <CardTitle>Payroll & HRIS</CardTitle>
          <CardDescription>
            Connect your payroll & HRIS provider to share read-only payroll & HRIS data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <MergeLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                integration="paychex"
                logoComponent={<PaychexIcon className="text-[#000000]" />}
              />
              <MergeLinkButton
                underwritableEntity={defaultUnderwritableEntity.id}
                logoComponent={<MergeIcon className="text-[#000000]" />}
              />
            </div>
            <LinkedMergeAccounts />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-4">
          <CardTitle>Tax Returns</CardTitle>
          <CardDescription>Upload your tax return documents</CardDescription>
        </CardHeader>
        <CardContent>
          <TaxReturns underwritableEntity={defaultUnderwritableEntity.id} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-4">
          <CardTitle>Leases</CardTitle>
          <CardDescription>Upload your lease documents</CardDescription>
        </CardHeader>
        <CardContent>
          <Leases underwritableEntity={defaultUnderwritableEntity.id} />
        </CardContent>
      </Card>
    </div>
  );
}
